/**
 * Button Component V2
 *
 * WCAG 2.1 Level AA + BFSG konform
 * Siehe: docs/frontend/design-rules.md (Tokens: docs/frontend/design-contract.md)
 */

import React, { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  /**
   * Button variant
   * @default 'primary'
   */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive' | 'danger' | 'warning' | 'success' | 'info' | 'default';

  /**
   * Button size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg' | 'icon';

  /**
   * Loading state (shows spinner, disables interaction)
   * @default false
   */
  loading?: boolean;

  /**
   * Icon on the left side
   */
  iconLeft?: React.ReactNode;

  /**
   * Icon on the right side
   */
  iconRight?: React.ReactNode;

  /**
   * Merge props onto child element (Radix Slot pattern)
   * @default false
   */
  asChild?: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Button Component
 *
 * Features:
 * - WCAG 2.1 AA compliant (4.5:1 contrast ratios)
 * - BFSG compliant (44x44px minimum touch target)
 * - Keyboard accessible (Enter, Space)
 * - Focus indicators (2px ring, 3:1 contrast)
 * - Loading state with spinner
 * - Icon support (left/right)
 * - Dark/Light mode support
 * - asChild support for Link composition
 *
 * @example
 * ```tsx
 * // Primary button
 * <Button variant="primary" onClick={handleClick}>
 *   Speichern
 * </Button>
 *
 * // With icon
 * <Button iconLeft={<Save />}>
 *   Speichern
 * </Button>
 *
 * // Loading state
 * <Button loading>
 *   Wird geladen...
 * </Button>
 *
 * // As Link
 * <Button asChild variant="outline">
 *   <Link to="/page">Go to Page</Link>
 * </Button>
 * ```
 */
export const Button = forwardRef<ElementRef<'button'>, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      iconLeft,
      iconRight,
      className,
      children,
      disabled,
      type = 'button',
      asChild = false,
      ...props
    },
    ref
  ) => {
    // Determine if button is disabled
    const isDisabled = disabled || loading;

    // Normalize variant aliases
    const normalizedVariant = variant === 'danger' ? 'destructive' : variant;

    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : type}
        disabled={asChild ? undefined : isDisabled}
        className={cn(
          // Base styles
          'inline-flex items-center justify-center gap-2',
          'font-medium rounded-md',
          'transition-all duration-200',

          // BFSG: Touch targets minimum 44x44px (h-11 = 44px)
          'min-h-11',

          // Focus styles (WCAG 2.1: 3:1 contrast minimum)
          'focus-visible:outline-none',
          'focus-visible:ring-2 focus-visible:ring-offset-2',
          'focus-visible:ring-primary-500',

          // Disabled state
          'disabled:pointer-events-none disabled:opacity-50',

          // Variant styles (Design Briefing v1.0 - Orange-Marke, kein Blau/Lila)
          // Primary - Orange-Gradient + Hover-Glow
          normalizedVariant === 'primary' && 'bg-gradient-primary text-white hover:shadow-glow active:brightness-95',

          // Secondary - Neutral Dark
          normalizedVariant === 'secondary' && 'bg-neutral-800 text-white hover:bg-neutral-700 active:bg-neutral-900 dark:bg-neutral-800 dark:hover:bg-neutral-700',

          // Outline - Neutral bg with border
          normalizedVariant === 'outline' && 'bg-white dark:bg-neutral-900 border-2 border-neutral-300 dark:border-neutral-700 text-neutral-900 dark:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:border-neutral-400 dark:hover:border-neutral-600',

          // Ghost - Transparent
          normalizedVariant === 'ghost' && 'bg-transparent text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100',

          // Destructive - Red (WCAG-Statusfarbe)
          normalizedVariant === 'destructive' && 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 dark:bg-red-600 dark:hover:bg-red-500',

          // Warning - Amber (WCAG-Statusfarbe)
          normalizedVariant === 'warning' && 'bg-amber-600 text-white hover:bg-amber-700 active:bg-amber-800 dark:bg-amber-600 dark:hover:bg-amber-500',

          // Success - Green
          normalizedVariant === 'success' && 'bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 dark:bg-emerald-600 dark:hover:bg-emerald-500',

          // Info - kein Blau lt. Briefing -> Orange-Marke
          normalizedVariant === 'info' && 'bg-gradient-primary text-white hover:shadow-glow active:brightness-95',

          // Default - Light neutral bg
          normalizedVariant === 'default' && 'bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 hover:bg-neutral-200 dark:hover:bg-neutral-700',

          // Size styles
          {
            'px-3 py-1.5 text-sm': size === 'sm',
            'px-4 py-2 text-base': size === 'md',
            'px-6 py-3 text-lg': size === 'lg',
            'h-10 w-10 p-0': size === 'icon',
          },

          // Custom className
          className
        )}
        // ARIA attributes for accessibility
        aria-disabled={isDisabled}
        aria-busy={loading}
        {...props}
      >
        {/* When asChild, pass children directly for Slot to work */}
        {asChild ? (
          children
        ) : (
          <>
            {/* Loading Spinner */}
            {loading && (
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}

            {/* Icon Left */}
            {!loading && iconLeft && <span aria-hidden="true">{iconLeft}</span>}

            {/* Button Text */}
            {children}

            {/* Icon Right */}
            {!loading && iconRight && <span aria-hidden="true">{iconRight}</span>}
          </>
        )}
      </Comp>
    );
  }
);

Button.displayName = 'Button';
